import React, { useRef, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBold, faItalic, faListUl } from '@fortawesome/free-solid-svg-icons';
import './RichTextField.css';

/**
 * RichTextField — minimal rich text editor (Jonas feedback 19.08:
 * "could we format free text with bold, italic, bullet").
 *
 * contentEditable + a three-button toolbar; no editor dependency. Output is
 * sanitized HTML restricted to a small whitelist (b/strong, i/em, ul/ol/li,
 * br, p, div) with every attribute stripped, so nothing script-able is ever
 * stored. Plain-text values (no '<') load and save exactly as before.
 *
 * Props:
 *  - value: HTML (or plain text) string
 *  - onChange(html): sanitized HTML on every edit
 *  - placeholder, maxTextLength (visible characters, not markup)
 */

const ALLOWED_TAGS = new Set(['B', 'STRONG', 'I', 'EM', 'UL', 'OL', 'LI', 'BR', 'P', 'DIV']);

// DOM-walking sanitizer: keeps whitelisted tags (attributes stripped),
// unwraps everything else into its children. Never uses innerHTML parsing
// of untrusted content into the live document.
export const sanitizeRichText = (html) => {
  if (!html) return '';
  const template = document.createElement('template');
  template.innerHTML = html;

  const clean = (node) => {
    const children = [...node.childNodes];
    for (const child of children) {
      if (child.nodeType === Node.ELEMENT_NODE) {
        clean(child);
        if (ALLOWED_TAGS.has(child.tagName)) {
          // Strip every attribute (style/class/on* included)
          for (const attr of [...child.attributes]) {
            child.removeAttribute(attr.name);
          }
        } else {
          // Unwrap: replace the element with its children
          while (child.firstChild) {
            node.insertBefore(child.firstChild, child);
          }
          node.removeChild(child);
        }
      } else if (child.nodeType !== Node.TEXT_NODE) {
        // Comments, CDATA, etc.
        node.removeChild(child);
      }
    }
  };

  clean(template.content);
  const wrapper = document.createElement('div');
  wrapper.appendChild(template.content.cloneNode(true));
  return wrapper.innerHTML;
};

// Visible character count (markup excluded)
export const richTextLength = (html) => {
  if (!html) return 0;
  const el = document.createElement('div');
  el.innerHTML = sanitizeRichText(html);
  return el.textContent.length;
};

const RichTextField = ({
  value = '',
  onChange,
  placeholder = '',
  maxTextLength = 10000,
}) => {
  const editorRef = useRef(null);

  // Load external value only when it differs from the editor's current
  // content — resetting innerHTML on every keystroke would jump the cursor.
  useEffect(() => {
    const el = editorRef.current;
    if (el && el.innerHTML !== (value || '')) {
      el.innerHTML = sanitizeRichText(value || '');
    }
  }, [value]);

  const emit = useCallback(() => {
    const el = editorRef.current;
    if (!el || !onChange) return;
    const html = sanitizeRichText(el.innerHTML);
    if (richTextLength(html) > maxTextLength) return; // over cap: don't emit
    onChange(html);
  }, [onChange, maxTextLength]);

  const exec = (command) => {
    editorRef.current?.focus();
    document.execCommand(command, false, null);
    emit();
  };

  return (
    <div className="rich-text-field">
      <div className="rich-text-field-toolbar">
        <button type="button" title="Bold" onMouseDown={(e) => { e.preventDefault(); exec('bold'); }}>
          <FontAwesomeIcon icon={faBold} />
        </button>
        <button type="button" title="Italic" onMouseDown={(e) => { e.preventDefault(); exec('italic'); }}>
          <FontAwesomeIcon icon={faItalic} />
        </button>
        <button type="button" title="Bullet list" onMouseDown={(e) => { e.preventDefault(); exec('insertUnorderedList'); }}>
          <FontAwesomeIcon icon={faListUl} />
        </button>
      </div>
      <div
        ref={editorRef}
        className="rich-text-field-editor"
        contentEditable
        data-placeholder={placeholder}
        onInput={emit}
        onBlur={emit}
        suppressContentEditableWarning
      />
    </div>
  );
};

export default RichTextField;
